var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef, useEffect, useMemo, useRef, useState, } from "react";
import { useAssetResolver } from "@/react/Webshop/providers/AssetResolver";
const getRequiredSize = (size) => {
    var _a;
    const sizes = [
        {
            size: 160,
            width: 160,
            height: 120,
        },
        {
            size: 320,
            width: 320,
            height: 160,
        },
        {
            size: 640,
            width: 640,
            height: 480,
        },
        {
            size: 800,
            width: 800,
            height: 640,
        },
        {
            size: 1280,
            width: 1280,
            height: 720,
        },
    ];
    for (const s of sizes) {
        if (s.width > ((_a = size === null || size === void 0 ? void 0 : size.width) !== null && _a !== void 0 ? _a : 0) || s.height > s.height) {
            return s.size;
        }
    }
    return 1280;
};
const placeHolder = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkqAcAAIUAgUW0RjgAAAAASUVORK5CYII=";
const EpmsImage = forwardRef(function EpmsImage(_a, ref) {
    var { uuid, effect = "original", urlCreator, onClick, size = 160, rotation = 0, lockedSize = false, watermark = true, forceVisible, onLoad } = _a, props = __rest(_a, ["uuid", "effect", "urlCreator", "onClick", "size", "rotation", "lockedSize", "watermark", "forceVisible", "onLoad"]);
    const imageRef = useRef(null);
    const [imageSize, setImageSize] = useState(size);
    const [isVisible, setIsVisible] = useState(false);
    const [retryCount, setRetryCount] = useState(0);
    const [requiredSize, setRequiredSize] = useState(0);
    const assetResolver = useAssetResolver();
    const retryTimerRef = useRef(null);
    const onCreateUrl = useMemo(() => {
        if (urlCreator) {
            return urlCreator;
        }
        return assetResolver.imageUrl;
    }, [assetResolver.imageUrl, urlCreator]);
    // This is triggered by resize observer:
    useEffect(() => {
        if (requiredSize && imageSize < requiredSize && !lockedSize) {
            setImageSize(requiredSize);
        }
    }, [imageSize, lockedSize, requiredSize]);
    const onImageLoad = (e) => {
        var _a;
        if (onLoad) {
            // Only propagnate onLoad if the url matches
            if (e.currentTarget.src ==
                onCreateUrl(uuid, imageSize, effect, rotation, watermark, retryCount)) {
                onLoad(e);
            }
        }
        if (imageRef && e.currentTarget.src != placeHolder && !lockedSize) {
            const size = (_a = imageRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            const requiredSize = getRequiredSize(size);
            if (requiredSize > imageSize) {
                setImageSize(requiredSize);
            }
        }
    };
    const imageSrc = useMemo(() => {
        if (!isVisible && !forceVisible) {
            return placeHolder;
        }
        const url = onCreateUrl(uuid, imageSize, effect, rotation, watermark, retryCount);
        return url;
    }, [
        isVisible,
        forceVisible,
        onCreateUrl,
        uuid,
        imageSize,
        effect,
        rotation,
        watermark,
        retryCount,
    ]);
    const onError = () => {
        let sleepTime = retryCount * 3;
        if (sleepTime > 30) {
            sleepTime = 30;
        }
        // Clear any active timer
        if (retryTimerRef.current) {
            clearTimeout(retryTimerRef.current);
        }
        retryTimerRef.current = setTimeout(() => {
            setRetryCount((rc) => {
                return rc + 1;
            });
        }, sleepTime * 1000);
    };
    // Clear any timer on unmount
    useEffect(() => {
        return () => {
            if (retryTimerRef.current) {
                clearTimeout(retryTimerRef.current);
            }
        };
    }, []);
    useEffect(() => {
        if (size > imageSize) {
            setImageSize(size);
        }
    }, [size, imageSize]);
    useEffect(() => {
        let observer;
        let resizeObserver;
        let didCancel = false;
        const currentImage = imageRef.current;
        if (currentImage) {
            if (typeof IntersectionObserver != "undefined") {
                observer = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        // when image is visible in the viewport + rootMargin
                        if (!didCancel &&
                            (entry.intersectionRatio > 0 || entry.isIntersecting)) {
                            setIsVisible(true);
                        }
                    });
                }, {
                    threshold: 0.01,
                    rootMargin: "75%",
                });
                observer.observe(currentImage);
            }
            else {
                // Old browsers fallback
                setIsVisible(true);
            }
            if (typeof ResizeObserver != "undefined") {
                resizeObserver = new ResizeObserver((entries) => {
                    // Use request animation frame to avoid ResizeObserver loop limit exceeded
                    window.requestAnimationFrame(() => {
                        if (!Array.isArray(entries) || !entries.length) {
                            return;
                        }
                        entries.forEach(() => {
                            // Don't use the event as it's not scaled (If we used scaled templates),
                            // element getBoundingClientRect is real size:
                            if (!currentImage) {
                                return;
                            }
                            const size = currentImage.getBoundingClientRect();
                            const requiredSize = getRequiredSize(size);
                            setRequiredSize(requiredSize);
                        });
                    });
                });
                resizeObserver.observe(currentImage);
            }
        }
        return () => {
            if (!currentImage) {
                return;
            }
            // on component unmount, we remove the listner
            if (observer && observer.unobserve) {
                observer.unobserve(currentImage);
            }
            if (resizeObserver) {
                resizeObserver.unobserve(currentImage);
            }
        };
    }, []);
    if (!uuid) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("img", Object.assign({ ref: (node) => {
                imageRef.current = node;
                if (typeof ref === "function")
                    ref(node);
                else if (ref)
                    ref.current = node;
            }, src: imageSrc, onLoad: onImageLoad, onError: onError, onClick: onClick }, props))));
});
export default EpmsImage;
